import { useMemo, useCallback } from 'react';

import { OktaAuth, AccessToken, IDToken } from '@okta/okta-auth-js';
import { useOktaAuth } from '@okta/okta-react';
import { useHistory, useLocation } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@@/auto/hooks/redux';
import {
  refreshOktaTokens,
  saveOktaIdToken,
  saveOktaAccessToken,
} from '@@/auto/store/actions';
import { getOktaAccessToken, getOktaIdToken } from '@@/auto/store/selectors';

import { useVerifyAuth as useVerifyAuthSDK } from './useVerifyAuth.sdk';

function useVerifyAuth() {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const cachedHistory = useMemo(() => history, [history]);
  const oktaAccessToken = useAppSelector(getOktaAccessToken);
  const oktaIdToken = useAppSelector(getOktaIdToken);
  const { oktaAuth, authState } = useOktaAuth();

  const handleRefreshOktaTokens = useCallback(
    (auth: OktaAuth) => refreshOktaTokens(auth)(dispatch),
    [dispatch]
  );

  const handleSaveOktaIdToken = useCallback(
    (token: IDToken) => saveOktaIdToken(token)(dispatch),
    [dispatch]
  );

  const handleSaveOktaAccessToken = useCallback(
    (token: AccessToken) => saveOktaAccessToken(token)(dispatch),
    [dispatch]
  );

  const onAuthError = useCallback(() => {
    const intendedPath = `${location.pathname}${location.search}`;
    cachedHistory.replace(`/login?redirectUrl=${intendedPath}`);
  }, [cachedHistory, location.pathname, location.search]);

  return useVerifyAuthSDK({
    oktaAuth,
    authState,
    oktaAccessToken,
    oktaIdToken,
    refreshOktaTokens: handleRefreshOktaTokens,
    saveOktaIdToken: handleSaveOktaIdToken,
    saveOktaAccessToken: handleSaveOktaAccessToken,
    onAuthError,
  });
}

export default useVerifyAuth;
